"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BrainMobileTabs from "./mobile/BrainMobileTabs";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import BrainDesktopDrop from "./BrainDesktopDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useWaveData } from "@/hooks/useWaveData";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { QueryKey } from "../react-query-wrapper/ReactQueryWrapper";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import MemesQuickVoteDialog from "./left-sidebar/waves/memes-quick-vote/MemesQuickVoteDialog";
import { getActiveWaveIdFromUrl, getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { markDropCloseNavigation } from "@/helpers/drop-close-navigation.helpers";
import CreateWaveModal from "@/components/waves/create-wave/CreateWaveModal";
import CreateDirectMessageModal from "@/components/waves/create-dm/CreateDirectMessageModal";
import { useAuth } from "@/components/auth/Auth";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import { useClosingDropId } from "@/hooks/useClosingDropId";
import { useMemesQuickVoteDialogController } from "@/hooks/useMemesQuickVoteDialogController";
import BrainMobileViewContent from "./mobile/BrainMobileViewContent";
import FloatingMemesQuickVoteTrigger from "./mobile/FloatingMemesQuickVoteTrigger";
import { BrainView } from "./mobile/brainMobileViews";
import { useBrainMobileActiveView } from "./mobile/useBrainMobileActiveView";
interface Props {
  readonly children: ReactNode;
}
const BrainMobile: React.FC<Props> = t0 => {
  const $ = _c(103);
  const {
    children
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const {
    connectedProfile
  } = useAuth();
  const {
    closeQuickVote,
    isQuickVoteOpen,
    openQuickVote,
    prefetchQuickVote,
    quickVoteSessionId
  } = useMemesQuickVoteDialogController();
  const [hydrated, setHydrated] = useState(false);
  const myStream = useMyStreamOptional();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setHydrated(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== searchParams) {
    t3 = searchParams.get("drop") ?? undefined;
    $[2] = searchParams;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const dropId = t3;
  const {
    effectiveDropId,
    beginClosingDrop
  } = useClosingDropId(dropId);
  let t4;
  let t5;
  if ($[4] !== effectiveDropId) {
    t4 = [QueryKey.DROP, {
      drop_id: effectiveDropId
    }];
    t5 = async () => {
      if (!effectiveDropId) {
        throw new Error("Cannot fetch drop without a drop id");
      }
      return await commonApiFetch({
        endpoint: `drops/${effectiveDropId}`
      });
    };
    $[4] = effectiveDropId;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  const t6 = !!effectiveDropId;
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      placeholderData: keepPreviousData,
      enabled: t6
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const {
    data: drop
  } = useQuery(t7);
  let t8;
  if ($[11] !== myStream?.activeWave.id || $[12] !== pathname || $[13] !== searchParams) {
    t8 = myStream?.activeWave.id ?? getActiveWaveIdFromUrl({
      pathname,
      searchParams
    }) ?? null;
    $[11] = myStream?.activeWave.id;
    $[12] = pathname;
    $[13] = searchParams;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const waveId = t8;
  let t9;
  if ($[15] !== isApp || $[16] !== pathname || $[17] !== router || $[18] !== searchParams) {
    t9 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[15] = isApp;
    $[16] = pathname;
    $[17] = router;
    $[18] = searchParams;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9 || $[21] !== waveId) {
    t10 = {
      waveId,
      onWaveNotFound: t9
    };
    $[20] = t9;
    $[21] = waveId;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const {
    data: wave
  } = useWaveData(t10);
  const {
    isMemesWave,
    isCurationWave,
    isRankWave,
    isDm
  } = useWave(wave);
  const {
    voting: t11,
    decisions: t12
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t11;
  const {
    firstDecisionDone
  } = t12;
  let t13;
  if ($[23] !== firstDecisionDone || $[24] !== isApp || $[25] !== isCompleted || $[26] !== isCurationWave || $[27] !== isMemesWave || $[28] !== isRankWave || $[29] !== pathname || $[30] !== searchParams || $[31] !== wave || $[32] !== waveId) {
    t13 = {
      firstDecisionDone,
      isApp,
      isCompleted,
      isCurationWave,
      isMemesWave,
      isRankWave,
      pathname,
      searchParams,
      wave,
      waveId
    };
    $[23] = firstDecisionDone;
    $[24] = isApp;
    $[25] = isCompleted;
    $[26] = isCurationWave;
    $[27] = isMemesWave;
    $[28] = isRankWave;
    $[29] = pathname;
    $[30] = searchParams;
    $[31] = wave;
    $[32] = waveId;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const {
    activeView,
    onViewChange
  } = useBrainMobileActiveView(t13);
  let t14;
  if ($[34] !== pathname || $[35] !== router || $[36] !== searchParams) {
    t14 = drop_0 => {
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", drop_0.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[34] = pathname;
    $[35] = router;
    $[36] = searchParams;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const onDropClick = t14;
  let t15;
  if ($[38] !== beginClosingDrop || $[39] !== dropId || $[40] !== pathname || $[41] !== router || $[42] !== searchParams) {
    t15 = () => {
      if (dropId) {
        beginClosingDrop(dropId);
      }
      markDropCloseNavigation();
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      params_1.delete("drop");
      const newUrl_0 = params_1.toString() ? `${pathname}?${params_1.toString()}` : pathname || getHomeRoute();
      router.replace(newUrl_0, {
        scroll: false
      });
    };
    $[38] = beginClosingDrop;
    $[39] = dropId;
    $[40] = pathname;
    $[41] = router;
    $[42] = searchParams;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const onDropClose = t15;
  let t16;
  if ($[44] !== drop || $[45] !== effectiveDropId) {
    t16 = !!effectiveDropId && !!drop && drop.id.toLowerCase() === effectiveDropId.toLowerCase();
    $[44] = drop;
    $[45] = effectiveDropId;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const isDropOpen = t16;
  const hasWave = Boolean(waveId);
  let t17;
  if ($[47] !== pathname || $[48] !== router || $[49] !== searchParams) {
    t17 = () => {
      const params_2 = new URLSearchParams(searchParams.toString() || "");
      params_2.delete("create");
      const base = pathname ?? "/";
      const next = params_2.toString() ? `${base}?${params_2.toString()}` : base;
      router.replace(next, {
        scroll: false
      });
    };
    $[47] = pathname;
    $[48] = router;
    $[49] = searchParams;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  const closeCreateOverlay = t17;
  let t18;
  bb0: {
    if (!isApp) {
      t18 = null;
      break bb0;
    }
    const createParam = searchParams.get("create");
    if (!createParam) {
      t18 = null;
      break bb0;
    }
    if (!connectedProfile) {
      t18 = null;
      break bb0;
    }
    if (createParam === "dm") {
      let t19;
      if ($[51] !== closeCreateOverlay || $[52] !== connectedProfile) {
        t19 = <CreateDirectMessageModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[51] = closeCreateOverlay;
        $[52] = connectedProfile;
        $[53] = t19;
      } else {
        t19 = $[53];
      }
      t18 = t19;
      break bb0;
    }
    if (createParam === "wave") {
      let t19;
      if ($[54] !== closeCreateOverlay || $[55] !== connectedProfile) {
        t19 = <CreateWaveModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[54] = closeCreateOverlay;
        $[55] = connectedProfile;
        $[56] = t19;
      } else {
        t19 = $[56];
      }
      t18 = t19;
      break bb0;
    }
    t18 = null;
  }
  const createOverlay = t18;
  const shouldMountFloatingQuickVoteEntry = isApp && hasWave && !!wave && activeView === BrainView.DEFAULT && !isDropOpen && !isDm;
  const shouldMountQuickVoteDialog = isQuickVoteOpen || shouldMountFloatingQuickVoteEntry || activeView === BrainView.WAVES;
  const dropOverlayClass = isApp ? "tw-fixed tw-inset-0 tw-z-[1010] tw-bg-black tailwind-scope" : "tw-absolute tw-inset-0 tw-z-[1010]";
  let t19;
  if ($[57] !== drop || $[58] !== dropOverlayClass || $[59] !== isDropOpen || $[60] !== onDropClose) {
    t19 = isDropOpen && <div className={dropOverlayClass}><BrainDesktopDrop drop={{
        type: DropSize.FULL,
        ...drop,
        stableKey: drop.id,
        stableHash: drop.id
      }} onClose={onDropClose} /></div>;
    $[57] = drop;
    $[58] = dropOverlayClass;
    $[59] = isDropOpen;
    $[60] = onDropClose;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== activeView || $[63] !== hasWave || $[64] !== hydrated || $[65] !== isApp || $[66] !== onViewChange || $[67] !== wave) {
    t20 = (hasWave || !isApp) && <BrainMobileTabs activeView={activeView} onViewChange={onViewChange} wave={wave} waveActive={hasWave} showWavesTab={hydrated} showStreamBack={hydrated} isApp={isApp} />;
    $[62] = activeView;
    $[63] = hasWave;
    $[64] = hydrated;
    $[65] = isApp;
    $[66] = onViewChange;
    $[67] = wave;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  let t21;
  let t22;
  let t23;
  let t24;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      opacity: 0,
      x: 20
    };
    t22 = {
      opacity: 1,
      x: 0
    };
    t23 = {
      opacity: 0,
      x: -20
    };
    t24 = {
      duration: 0.2,
      ease: "easeInOut"
    };
    $[69] = t21;
    $[70] = t22;
    $[71] = t23;
    $[72] = t24;
  } else {
    t21 = $[69];
    t22 = $[70];
    t23 = $[71];
    t24 = $[72];
  }
  let t25;
  if ($[73] !== openQuickVote || $[74] !== prefetchQuickVote || $[75] !== shouldMountFloatingQuickVoteEntry) {
    t25 = shouldMountFloatingQuickVoteEntry && <FloatingMemesQuickVoteTrigger onOpenQuickVote={openQuickVote} onPrefetchQuickVote={prefetchQuickVote} />;
    $[73] = openQuickVote;
    $[74] = prefetchQuickVote;
    $[75] = shouldMountFloatingQuickVoteEntry;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] !== activeView || $[78] !== children || $[79] !== isCurationWave || $[80] !== isMemesWave || $[81] !== isRankWave || $[82] !== onDropClick || $[83] !== openQuickVote || $[84] !== prefetchQuickVote || $[85] !== wave || $[86] !== waveId) {
    t26 = <BrainMobileViewContent activeView={activeView} activeWaveId={waveId} isCurationWave={isCurationWave} isMemesWave={isMemesWave} isRankWave={isRankWave} onDropClick={onDropClick} onOpenQuickVote={openQuickVote} onPrefetchQuickVote={prefetchQuickVote} wave={wave}>{children}</BrainMobileViewContent>;
    $[77] = activeView;
    $[78] = children;
    $[79] = isCurationWave;
    $[80] = isMemesWave;
    $[81] = isRankWave;
    $[82] = onDropClick;
    $[83] = openQuickVote;
    $[84] = prefetchQuickVote;
    $[85] = wave;
    $[86] = waveId;
    $[87] = t26;
  } else {
    t26 = $[87];
  }
  let t27;
  if ($[88] !== activeView || $[89] !== t25 || $[90] !== t26) {
    t27 = <AnimatePresence mode="wait"><motion.div key={activeView} initial={t21} animate={t22} exit={t23} transition={t24} className="tw-relative tw-min-w-0 tw-flex-1">{t25}{t26}</motion.div></AnimatePresence>;
    $[88] = activeView;
    $[89] = t25;
    $[90] = t26;
    $[91] = t27;
  } else {
    t27 = $[91];
  }
  let t28;
  if ($[92] !== closeQuickVote || $[93] !== isQuickVoteOpen || $[94] !== quickVoteSessionId || $[95] !== shouldMountQuickVoteDialog) {
    t28 = shouldMountQuickVoteDialog && <MemesQuickVoteDialog isOpen={isQuickVoteOpen} sessionId={quickVoteSessionId} onClose={closeQuickVote} />;
    $[92] = closeQuickVote;
    $[93] = isQuickVoteOpen;
    $[94] = quickVoteSessionId;
    $[95] = shouldMountQuickVoteDialog;
    $[96] = t28;
  } else {
    t28 = $[96];
  }
  let t29;
  if ($[97] !== createOverlay || $[98] !== t19 || $[99] !== t20 || $[100] !== t27 || $[101] !== t28) {
    t29 = <div className="tw-relative tw-flex tw-h-full tw-flex-col">{createOverlay}{t19}{t20}{t27}{t28}</div>;
    $[97] = createOverlay;
    $[98] = t19;
    $[99] = t20;
    $[100] = t27;
    $[101] = t28;
    $[102] = t29;
  } else {
    t29 = $[102];
  }
  return t29;
};
export default BrainMobile;